import { useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { ClickableT } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { useTranslations } from 'next-intl';
import { ClickableOrReadonlyWrapper } from '../ui/ClickableOrReadonlyWrapper';
import { TextSkeleton } from './TextSkeleton';

const ICON_SPACING = 15;

interface ResponseProps extends Omit<ClickableT, 'onClick'> {
  responseText: string;
  index: number;
  participantDisplayName?: string | null;
  onClick: (index: number) => void;
  answerFontSize?: string | undefined;
  controls?: React.ReactNode;
  controlsOnHover?: React.ReactNode;
  isTextHidden: boolean;
  shouldAnimate: boolean;
  isReadonly: boolean;
}

export const Response = ({
  responseText,
  index,
  onClick,
  answerFontSize,
  controls,
  controlsOnHover,
  participantDisplayName,
  isTextHidden,
  shouldAnimate,
  isReadonly,
  ...props
}: ResponseProps) => {
  const [shouldShowControlsOnHover, setShouldShowControlsOnHover] =
    useState(false);
  const translate = useTranslations('presenting.legacy');

  return (
    <Box
      width="100%"
      onMouseEnter={() => setShouldShowControlsOnHover(true)}
      onMouseLeave={() => setShouldShowControlsOnHover(false)}
      aria-hidden={isTextHidden}
    >
      <ClickableOrReadonlyWrapper
        aria-label={translate('actions.show_single_question')}
        id={`flow-item-${index}`}
        onClick={isReadonly ? null : () => onClick(index)}
        width="100%"
        p="space10"
        borderRadius="3xl"
        bg={shouldShowControlsOnHover ? 'neutral' : 'neutralWeak'}
        color="text"
        {...props}
      >
        <Box width="100%">
          <Text
            textAlign="start"
            fontSize={answerFontSize ?? '175'}
            lineHeight="snug"
            color="inherit"
            style={{
              opacity: isTextHidden ? 0 : 1,
              transition: shouldAnimate ? 'opacity 0.2s ease' : 'none',
            }}
          >
            {responseText}
          </Text>
          <TextSkeleton
            isVisible={isTextHidden}
            shouldAnimate={shouldAnimate}
            color={shouldShowControlsOnHover ? 'neutralWeak' : 'neutral'}
          />
        </Box>
        {participantDisplayName && (
          <Text
            fontSize="125"
            mt="space3"
            color="textWeak"
            style={{
              opacity: isTextHidden ? 0 : 1,
              transition: shouldAnimate ? 'opacity 0.2s ease' : 'none',
            }}
          >
            {participantDisplayName}
          </Text>
        )}
      </ClickableOrReadonlyWrapper>
      {controls && !isReadonly && (
        <Box
          position="absolute"
          height={`calc(100% - ${ICON_SPACING * 2}px)`}
          justifyContent="space-between"
          style={{
            top: `${ICON_SPACING}px`,
            right: `${ICON_SPACING}px`,
          }}
        >
          {controls}
        </Box>
      )}
      {controlsOnHover && !isReadonly && (
        <Box
          position="absolute"
          justifyContent="space-between"
          color="onNeutralWeak"
          style={{
            top: `${ICON_SPACING}px`,
            right: `${ICON_SPACING}px`,
            opacity: shouldShowControlsOnHover ? 1 : 0,
            transition: 'opacity 0.5s ease',
          }}
        >
          {controlsOnHover}
        </Box>
      )}
    </Box>
  );
};
